import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  forwardRef,
} from 'react';
import { bfHaptic, setChapterSwipeGestureActive } from './BfHooks';
import {
  clearReaderTextSelection,
  isChapterSwipeBlockedTarget,
  isHorizontalSwipeIntent,
} from '../../../utils/readerTextSelection';

const SWIPE_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';
const SNAP_MS = 220;
const COMMIT_MS = 280;
const MIN_DX = 40;
const LOCK_DX = 8;
const COMMIT_REVEAL = 0.30;

function isSwipeBlockedTarget(target) {
  return isChapterSwipeBlockedTarget(target);
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function getChapterScrollOffset(root) {
  const scrollY = window.scrollY || window.pageYOffset || 0;
  const rootTop = root.getBoundingClientRect().top + scrollY;
  return Math.max(0, Math.round(scrollY - rootTop));
}

function applyCurrentPanelOffset(root, offset) {
  const el = root.querySelector('.bf-chapter-swipe-panel--current .bf-chapter-swipe-panel__content');
  if (!el) return;
  el.style.transform = offset ? `translateY(${-offset}px)` : '';
}

function clearCurrentPanelOffset(root) {
  applyCurrentPanelOffset(root, 0);
}

/**
 * Горизонтальный трек с 3 панелями: [пред. глава] [текущая] [след. глава].
 * Каждая панель — 100% ширины viewport, overflow: hidden.
 * Трек двигается через translateX, chapters начинаются с 1-го стиха.
 */
const BfChapterSwipeViewport = forwardRef(function BfChapterSwipeViewport(
  {
    enabled = true,
    chapterKey,
    allowPrev = false,
    allowNext = false,
    prevPanel,
    currentPanel,
    nextPanel,
    onSwipeEngaged,
    onNavigatePrev,
    onNavigateNext,
  },
  ref
) {
  const rootRef = useRef(null);
  const trackRef = useRef(null);
  const allowPrevRef = useRef(allowPrev);
  const allowNextRef = useRef(allowNext);
  const onSwipeEngagedRef = useRef(onSwipeEngaged);
  const onNavigatePrevRef = useRef(onNavigatePrev);
  const onNavigateNextRef = useRef(onNavigateNext);
  const commitInFlightRef = useRef(false);
  const frozenScrollYRef = useRef(0);
  const scrollLockedRef = useRef(false);

  allowPrevRef.current = allowPrev;
  allowNextRef.current = allowNext;
  onSwipeEngagedRef.current = onSwipeEngaged;
  onNavigatePrevRef.current = onNavigatePrev;
  onNavigateNextRef.current = onNavigateNext;

  /** Сбросить трек в центральную позицию без анимации. */
  const resetTrack = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const w = el.parentElement
      ? Math.round(el.parentElement.getBoundingClientRect().width || window.innerWidth || 360)
      : Math.round(window.innerWidth || 360);
    el.style.transition = 'none';
    el.style.transform = `translateX(-${w}px)`;
    /* Force reflow to apply the reset immediately */
    void el.offsetHeight;
  }, []);

  const unlockPageScroll = useCallback((restoreScroll = true) => {
    const root = rootRef.current;
    if (root) clearCurrentPanelOffset(root);

    if (!scrollLockedRef.current) return;

    const y = frozenScrollYRef.current;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    scrollLockedRef.current = false;

    if (restoreScroll) {
      requestAnimationFrame(() => {
        window.scrollTo(0, y);
      });
    }
  }, []);

  const lockPageScroll = useCallback((root) => {
    if (scrollLockedRef.current) return;

    const scrollY = window.scrollY || window.pageYOffset || 0;
    const chapterOffset = getChapterScrollOffset(root);

    frozenScrollYRef.current = scrollY;
    scrollLockedRef.current = true;

    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';

    root.classList.add('bf-chapter-swipe-dragging');
    applyCurrentPanelOffset(root, chapterOffset);
  }, []);

  /** Сбросить весь UI свайпа. */
  const resetSwipeUi = useCallback(
    (restoreScroll = true) => {
      resetTrack();
      commitInFlightRef.current = false;
      const root = rootRef.current;
      if (root) {
        root.classList.remove('bf-chapter-swipe-dragging');
        setChapterSwipeGestureActive(false, root);
      }
      unlockPageScroll(restoreScroll);
    },
    [resetTrack, unlockPageScroll]
  );

  /** После завершения свайпа — мгновенный сброс в центр. */
  const settleAfterSwipe = useCallback(() => {
    resetTrack();
    commitInFlightRef.current = false;
    const root = rootRef.current;
    if (root) {
      root.classList.remove('bf-chapter-swipe-dragging');
      setChapterSwipeGestureActive(false, root);
    }
    unlockPageScroll(false);
  }, [resetTrack, unlockPageScroll]);

  useImperativeHandle(ref, () => ({
    resetToCenter() {
      resetSwipeUi();
    },
    settleAfterSwipe() {
      settleAfterSwipe();
    },
  }));

  useEffect(() => {
    if (commitInFlightRef.current) return;
    resetSwipeUi();
  }, [chapterKey, resetSwipeUi]);

  useEffect(() => {
    if (enabled) return undefined;
    resetSwipeUi();
    return undefined;
  }, [enabled, resetSwipeUi]);

  useEffect(() => {
    const handleResize = () => {
      if (commitInFlightRef.current) return;
      resetTrack();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [resetTrack]);

  useEffect(
    () => () => {
      unlockPageScroll(false);
    },
    [unlockPageScroll]
  );

  /** Сброс «застрявшего» свайпа (потеря pointer, уход в фон). */
  useEffect(() => {
    const recoverStuckSwipe = () => {
      const root = rootRef.current;
      if (!root?.classList.contains('bf-chapter-swipe-dragging')) return;
      if (commitInFlightRef.current) return;
      resetSwipeUi();
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') recoverStuckSwipe();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', recoverStuckSwipe);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', recoverStuckSwipe);
    };
  }, [resetSwipeUi]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !enabled) return undefined;

    let startX = null;
    let startY = null;
    let horizontalLock = false;
    let activePointerId = null;
    let windowBound = false;
    let panelWidth = 0;
    let activeDir = null;
    let snapTimerId = null;

    const clearSnapTimer = () => {
      if (snapTimerId != null) {
        window.clearTimeout(snapTimerId);
        snapTimerId = null;
      }
    };

    const measurePanel = () => {
      const rect = root.getBoundingClientRect();
      panelWidth = Math.round(rect.width) || Math.round(window.innerWidth || 360);
    };

    const releasePointerCapture = () => {
      if (activePointerId == null) return;
      try {
        if (root.hasPointerCapture?.(activePointerId)) {
          root.releasePointerCapture(activePointerId);
        }
      } catch {
        /* no-op */
      }
    };

    /**
     * Обновить позицию трека.
     * offset: -1 (пред. полностью видна) … 0 (текущая в центре) … 1 (след. полностью видна)
     */
    const setTrackPosition = (offset, animate = false, durationMs = SNAP_MS) => {
      const el = trackRef.current;
      if (!el) return;
      const w = panelWidth || Math.round(root.getBoundingClientRect().width || window.innerWidth || 360);
      const clampedOffset = clamp(offset, -1, 1);
      const translateX = -(1 + clampedOffset) * w;
      el.style.transition = animate ? `transform ${durationMs}ms ${SWIPE_EASE}` : 'none';
      el.style.transform = `translateX(${translateX}px)`;
    };

    const getOffsetFromClientX = (clientX) => {
      if (startX == null || !panelWidth) return 0;
      const dx = clientX - startX;
      /* Свайп вправо → пред. глава (offset отрицательный) */
      if (activeDir === 'prev') return clamp(-dx / panelWidth, -1, 1);
      /* Свайп влево → след. глава (offset положительный) */
      if (activeDir === 'next') return clamp(-dx / panelWidth, -1, 1);
      return 0;
    };

    const snapBack = () => {
      clearSnapTimer();
      setTrackPosition(0, true, SNAP_MS);
      snapTimerId = window.setTimeout(() => {
        snapTimerId = null;
        resetSwipeUi();
      }, SNAP_MS + 50);
    };

    const commit = (dir, navigateFn) => {
      if (commitInFlightRef.current) return;
      commitInFlightRef.current = true;

      const targetOffset = dir === 'next' ? 1 : -1;
      setTrackPosition(targetOffset, true, COMMIT_MS);

      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        clearSnapTimer();
        root.classList.remove('bf-chapter-swipe-dragging');
        clearCurrentPanelOffset(root);
        setChapterSwipeGestureActive(false, root);
        if (scrollLockedRef.current) {
          document.body.style.position = '';
          document.body.style.top = '';
          document.body.style.left = '';
          document.body.style.right = '';
          document.body.style.width = '';
          scrollLockedRef.current = false;
        }
        navigateFn();
      };

      const track = trackRef.current;
      if (track) {
        const onEnd = (ev) => {
          if (ev.target !== track || ev.propertyName !== 'transform') return;
          track.removeEventListener('transitionend', onEnd);
          finish();
        };
        track.addEventListener('transitionend', onEnd);
      }

      snapTimerId = window.setTimeout(finish, COMMIT_MS + 120);
    };

    const endTracking = () => {
      releasePointerCapture();
      startX = null;
      startY = null;
      horizontalLock = false;
      activePointerId = null;
      activeDir = null;
    };

    const unbindWindow = () => {
      if (!windowBound) return;
      windowBound = false;
      window.removeEventListener('pointermove', onPointerMove, moveOpts);
      window.removeEventListener('pointerup', onPointerUp, upOpts);
      window.removeEventListener('pointercancel', onPointerCancel, upOpts);
    };

    const bindWindow = () => {
      if (windowBound) return;
      windowBound = true;
      window.addEventListener('pointermove', onPointerMove, moveOpts);
      window.addEventListener('pointerup', onPointerUp, upOpts);
      window.addEventListener('pointercancel', onPointerCancel, upOpts);
    };

    const lockHorizontal = (clientX, clientY) => {
      if (horizontalLock || startX == null || startY == null) return false;
      const dx = clientX - startX;
      const dy = clientY - startY;
      const adx = Math.abs(dx);
      const ady = Math.abs(dy);
      if (adx < LOCK_DX) return false;
      if (ady > adx * 0.75) return false;

      if (dx > 0 && !allowPrevRef.current) return false;
      if (dx < 0 && !allowNextRef.current) return false;

      clearReaderTextSelection();
      horizontalLock = true;
      measurePanel();
      activeDir = dx > 0 ? 'prev' : 'next';
      lockPageScroll(root);
      setChapterSwipeGestureActive(true, root);
      onSwipeEngagedRef.current?.();

      if (activePointerId != null) {
        try {
          root.setPointerCapture(activePointerId);
        } catch {
          /* no-op */
        }
      }

      setTrackPosition(getOffsetFromClientX(clientX));
      return true;
    };

    const onPointerMove = (e) => {
      if (activePointerId == null || e.pointerId !== activePointerId) return;
      if (startX == null) return;

      if (!horizontalLock) {
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        const adx = Math.abs(dx);
        const ady = Math.abs(dy);
        if (ady >= 12 && ady > adx * 1.2) {
          unbindWindow();
          endTracking();
          resetSwipeUi();
          return;
        }
        if (!isHorizontalSwipeIntent(dx, dy, LOCK_DX)) return;
        if (!lockHorizontal(e.clientX, e.clientY)) return;
      }

      clearReaderTextSelection();
      e.preventDefault();
      setTrackPosition(getOffsetFromClientX(e.clientX));
    };

    const onPointerUp = (e) => {
      if (activePointerId == null || e.pointerId !== activePointerId) return;
      unbindWindow();

      const sx = startX;
      const wasLock = horizontalLock;
      const dir = activeDir;
      const dx = sx != null ? e.clientX - sx : 0;
      const dy = startY != null ? e.clientY - startY : 0;
      const offset = wasLock && dir ? getOffsetFromClientX(e.clientX) : 0;
      const reveal = Math.abs(offset);

      endTracking();

      if (!wasLock || sx == null || !dir) {
        resetSwipeUi();
        return;
      }

      const adx = Math.abs(dx);
      const ady = Math.abs(dy);
      const shouldCommit =
        reveal >= COMMIT_REVEAL ||
        (adx >= MIN_DX && adx > ady * 1.2 && !(ady > 90 && ady > adx));

      if (shouldCommit && dir === 'next' && onNavigateNextRef.current) {
        bfHaptic('light');
        commit('next', onNavigateNextRef.current);
        return;
      }
      if (shouldCommit && dir === 'prev' && onNavigatePrevRef.current) {
        bfHaptic('light');
        commit('prev', onNavigatePrevRef.current);
        return;
      }
      snapBack();
    };

    const onPointerCancel = () => {
      unbindWindow();
      endTracking();
      snapBack();
    };

    const onPointerDown = (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (activePointerId != null || commitInFlightRef.current) return;
      if (isSwipeBlockedTarget(e.target)) return;

      clearSnapTimer();
      clearReaderTextSelection();
      resetSwipeUi();

      activePointerId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
      horizontalLock = false;
      activeDir = null;
      panelWidth = 0;
      bindWindow();
    };

    const captureOpts = { capture: true };
    const moveOpts = { capture: true, passive: false };
    const upOpts = { capture: true };

    root.addEventListener('pointerdown', onPointerDown, captureOpts);

    return () => {
      unbindWindow();
      root.removeEventListener('pointerdown', onPointerDown, captureOpts);
      clearSnapTimer();
    };
  }, [enabled, resetSwipeUi, lockPageScroll]);

  return (
    <div ref={rootRef} className="bf-chapter-swipe-root">
      <div className="bf-chapter-swipe-viewport">
        <div ref={trackRef} className="bf-chapter-swipe-track">
          {/* Предыдущая глава */}
          <div className="bf-chapter-swipe-panel bf-chapter-swipe-panel--prev">
            <div className="bf-chapter-swipe-panel__content verses-container w-full">
              {prevPanel}
            </div>
          </div>

          {/* Текущая глава */}
          <div className="bf-chapter-swipe-panel bf-chapter-swipe-panel--current">
            <div className="bf-chapter-swipe-panel__content verses-container w-full">
              {currentPanel}
            </div>
          </div>

          {/* Следующая глава */}
          <div className="bf-chapter-swipe-panel bf-chapter-swipe-panel--next">
            <div className="bf-chapter-swipe-panel__content verses-container w-full">
              {nextPanel}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default BfChapterSwipeViewport;
